import type { Client } from '../../api/client.js';
import { SIGNATURE_CABINET, type Fil, type MessageSortant } from './contrat.js';
import { phraseErreur } from './erreurs.js';

/** Tout message sortant se termine par « Cabinet MAAT », une seule fois. */
export function corpsSortant(texte: string): string {
  const net = texte.trim();
  return net.endsWith(SIGNATURE_CABINET) ? net : `${net}\n\n${SIGNATURE_CABINET}`;
}

const duree = (s: number): string => `${Math.floor(s / 60)} min ${String(Math.round(s % 60)).padStart(2, '0')} s`;

/** Libellé d'aperçu d'une pièce jointe : type, pages ou durée. */
export function resumePiece(piece: { mime: string; pages: number | null; durationS: number | null }): string {
  if (piece.mime === 'application/pdf') return piece.pages === null ? 'PDF' : `PDF, ${piece.pages} page${piece.pages > 1 ? 's' : ''}`;
  if (piece.mime.startsWith('video/')) return piece.durationS === null ? 'Vidéo' : `Vidéo, ${duree(piece.durationS)}`;
  if (piece.mime.startsWith('audio/')) return piece.durationS === null ? 'Vocal' : `Vocal, ${duree(piece.durationS)}`;
  if (piece.mime.startsWith('image/')) return 'Photo';
  return 'Document';
}

interface MandantConnu {
  noms: string[];
  sex: 'F' | 'M' | 'X' | null;
}

const echapper = (n: string): string => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Nom d'usage (premier prénom) et prénoms complets : le second prénom seul n'est pas retenu, il peut être celui d'un autre héritier. */
function nomsDe(firstNames: string): string[] {
  const complet = firstNames.trim().replace(/\s+/g, ' ');
  const premier = complet.split(' ')[0] ?? '';
  return [...new Set([complet, premier].filter((n) => n.length >= 2))];
}

const nomme = (texte: string, noms: readonly string[]): boolean =>
  noms.some((n) => new RegExp(`(?<![\\p{L}])${echapper(n)}(?![\\p{L}])`, 'iu').test(texte));

const phraseMandantNomme = (sex: MandantConnu['sex']): string =>
  `Ce message nomme ${sex === 'F' ? 'la mandante' : 'le mandant'}. Retirez son nom avant l’envoi.`;

export type ResultatEnvoi = { ok: true; message: string } | { ok: false; message: string };

const PHRASE_LECTURE = 'Le contrôle du nom du mandant n’a pas pu être fait : rien n’est parti. Réessayez dans un instant.';
const PHRASE_FICHE =
  'La fiche reçue ne correspond pas au mandant du dossier : le contrôle de son nom est impossible, rien n’est parti. Faites vérifier la fiche du mandant avant de répondre.';

/** Issue du contrôle : aucun mandant à protéger, le mandant connu, ou un refus (échec fermé). */
type Controle = { mandant: MandantConnu | null } | { refus: string };

/**
 * Le mandant du dossier, sauf quand le fil est celui du mandant lui-même (le nommer à lui-même reste permis).
 * Échec fermé : une lecture en échec, un dossier lu sans le champ du mandant ou une fiche qui n'est pas celle du
 * mandant demandé (doublons fusionnés, par exemple) refusent l'envoi.
 */
async function controlerMandant(client: Client, dossierId: string, personId: string): Promise<Controle> {
  let dossier: { mandantPersonId?: string | null };
  try {
    dossier = await client.appeler<{ mandantPersonId?: string | null }>('dossiers.lire', { parametres: { dossierId } });
  } catch {
    return { refus: PHRASE_LECTURE };
  }
  const mandantId = dossier.mandantPersonId;
  if (mandantId === undefined) return { refus: PHRASE_LECTURE };
  if (mandantId === null || mandantId === personId) return { mandant: null };
  let p: { id?: unknown; firstNames?: unknown; sex?: MandantConnu['sex'] };
  try {
    p = await client.appeler<typeof p>('genealogie.personne.lire', { parametres: { personId: mandantId } });
  } catch {
    return { refus: PHRASE_LECTURE };
  }
  if (p.id !== mandantId || typeof p.firstNames !== 'string') return { refus: PHRASE_FICHE };
  return { mandant: { noms: nomsDe(p.firstNames), sex: p.sex ?? null } };
}

/** Répond dans un fil rattaché. Aucun appel si le fil n'est pas rattaché ou si le texte est vide. */
export async function envoyerMessage(client: Client, fil: Fil, texte: string): Promise<ResultatEnvoi> {
  if (fil.dossierId === null) {
    return { ok: false, message: 'Rattachez d’abord ce fil à un dossier et à une personne avant de répondre.' };
  }
  if (fil.personId === null) {
    return {
      ok: false,
      message: 'Ce fil est rattaché à un organisme : la réponse ne part pas depuis la boîte, rattachez-le à une personne pour répondre.',
    };
  }
  if (texte.trim() === '') return { ok: false, message: 'Écrivez votre message avant de l’envoyer.' };
  // Sans pouvoir vérifier, rien ne part : le mandant ne doit jamais être nommé aux autres héritiers.
  const controle = await controlerMandant(client, fil.dossierId, fil.personId);
  if ('refus' in controle) return { ok: false, message: controle.refus };
  if (controle.mandant && nomme(texte, controle.mandant.noms)) return { ok: false, message: phraseMandantNomme(controle.mandant.sex) };
  try {
    const envoye = await client.appeler<MessageSortant>('messagerie.envoyer', {
      corps: {
        channelAccountId: fil.channelAccount.id,
        personId: fil.personId,
        dossierId: fil.dossierId,
        templateCode: null,
        body: corpsSortant(texte),
        scheduledAt: null,
        attachmentIds: [],
      },
    });
    return {
      ok: true,
      message:
        envoye.state === 'pending_validation'
          ? 'Message préparé : il partira après la validation d’Isabelle.'
          : 'Message enregistré : il partira aux heures d’envoi prévues.',
    };
  } catch (e) {
    return { ok: false, message: phraseErreur(e) };
  }
}
